import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import ActualUseContent from './ActualUseContent';
import CreateModal from '../components/CreateModal';
import UpdateModal from '../components/UpdateModal';

const form = {
  keyword: '',
  rptax_land_classification_id: ''
};

function ActualUseContainer({
  match,
  history,
  getActualUseList,
  actualUse,
  isLoading,
  removeActualUse,
  actualUsePager,
  getSingleActualUse,
  getLandClassificationsList,
  landClassificationsList
}) {
  const [formData, setFormData] = useState(form);
  const [filter, setFilter] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateModal />,
      title: 'Add Actual Use',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <UpdateModal updateList={data.attributes} id={data.id} />,
      title: 'Update Actual Use',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove Actual Use Entry?',
      () => {
        removeActualUse(data.id, () => {
          getActualUseList();
          const id = _.get(actualUse, '0.id');
          history.push(`${id}`);
          getSingleActualUse(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  useEffect(() => {
    getActualUseList();
    getLandClassificationsList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc',
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      getActualUseList({ ...filter, q: formData.keyword });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);

  const handleOnChangeSelect = key => e => {
    setFilter({
      ...formData,
      [key]: e.value,
      rptax_land_classification_id: e.value,
    });
    getActualUseList({ ...filter, rptax_land_classification_id: e.value, q: formData.keyword });
  };

  const selectOptions = landClassificationsList?.map(x => ({
    label: _.get(x, 'attributes.name'),
    value: x.id,
    id: x.id,
  }));

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:actualUseId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Actual Use"
            getData={getActualUseList}
            statusFilter
            statusOptions={selectOptions}
            setStatusFilter={handleOnChangeSelect(
              _.get(formData, 'rptax_land_classification_id')
            ) || []}
            status={_.get(filter, 'rptax_land_classification_id') || ''}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:actualUseId?`}
            listName="actualUse"
            label="Actual Use "
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.name')}</div>
            )}
            isLoading={isLoading}
            selected={params.actualUseId}
            data={actualUse}
            dataPager={actualUsePager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:actualUseId?`}
                    render={() => (
                      <ActualUseContent
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:actualUseId?`}
                  render={() =>
                    !params.actualUseId && actualUse.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(actualUse, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

ActualUseContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getActualUseList: PropTypes.instanceOf(Function).isRequired,
  actualUse: PropTypes.instanceOf(Array).isRequired,
  actualUsePager: PropTypes.instanceOf(Object).isRequired,
  removeActualUse: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleActualUse: PropTypes.instanceOf(Function).isRequired,
  getLandClassificationsList: PropTypes.instanceOf(Function).isRequired,
  landClassificationsList: PropTypes.instanceOf(Array).isRequired
};

const mapStateToProps = ({ api }) => ({
  actualUse: _.get(api, `${c.GET_ACTUAL_USES}.list`) || [],
  actualUsePager: _.get(api, `${c.GET_ACTUAL_USES}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_ACTUAL_USES) > -1,
  landClassificationsList: _.get(api, `${c.GET_LAND_CLASSIFICATION_LIST}.list`)
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(ActualUseContainer);
